import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Icon from "./Icon";
import BrandLogo from "./BrandLogo";
import TopBar from "./TopBar";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/academy", label: "Our Academy" },
  { to: "/programs", label: "Programs" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

function getHeaderStyle(pathname) {
  if (pathname === "/pricing") return { blur: true };
  if (pathname === "/academy") return { startContainer: true };
  if (pathname === "/blog") return { wideNav: true };
  if (pathname === "/contact" || pathname.startsWith("/programs/hifz"))
    return { wideNav: true };
  return {};
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showTopBar, setShowTopBar] = useState(true);
  const lastScrollY = useRef(0);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Keep top bar shown near top or when mobile menu is active
      if (currentScrollY <= 20 || mobileOpen) {
        setShowTopBar(true);
      } else if (currentScrollY > lastScrollY.current && currentScrollY > 40) {
        // Hide top bar when scrolling down
        setShowTopBar(false);
      } else if (currentScrollY < lastScrollY.current) {
        // Show top bar when scrolling up
        setShowTopBar(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [mobileOpen]);

  const style = getHeaderStyle(pathname);
  const navClass = style.wideNav ? "hidden lg:flex" : "hidden md:flex";
  const menuClass = style.wideNav ? "lg:hidden" : "md:hidden";

  return (
    <header className="sticky top-0 z-50 shadow-sm transition-colors duration-300">
      <TopBar isVisible={showTopBar} />

      <div
        className={`border-b border-outline-variant ${
          style.blur ? "celestial-blur" : "bg-surface/95 backdrop-blur-md"
        }`}
      >

      <div className="flex justify-between items-center gap-2 page-x py-2.5 sm:py-3 md:py-3 max-w-max-width mx-auto min-w-0">
        <Link
          to="/"
          className="flex items-center min-w-0 shrink"
          aria-label="Online Quran Universe Academy home"
        >
          <BrandLogo
            wrapperClassName="h-12 sm:h-13 md:h-14 w-[8rem] sm:w-[9.5rem] md:w-[10.5rem]"
            imageClassName="scale-[2.15]"
          />
        </Link>

        <nav
          className={`${navClass} gap-6 lg:gap-8 items-center shrink-0`}
          aria-label="Main navigation"
        >
          {navItems.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                isActive
                  ? "text-primary font-bold border-b-2 border-primary pb-1 font-body-md transition-colors whitespace-nowrap"
                  : "text-on-surface-variant hover:text-primary transition-colors font-body-md whitespace-nowrap"
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 md:gap-4 shrink-0">
          <Link
            to="/contact"
            className={`inline-flex px-3.5 sm:px-4 md:px-5 py-2 rounded-full font-label-md hover:opacity-90 active:scale-95 transition-all whitespace-nowrap text-label-md ${
              style.startContainer
                ? "bg-primary-container text-on-primary-container shadow-sm"
                : "bg-primary text-on-primary"
            }`}
          >
            <span className="hidden sm:inline">Start Free Trial</span>
            <span className="sm:hidden">Trial</span>
          </Link>
          <button
            type="button"
            className={`${menuClass} text-primary p-2 -mr-1`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            <Icon name={mobileOpen ? "close" : "menu"} className="text-2xl" />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav
          className={`${style.wideNav ? "lg:hidden" : "md:hidden"} border-t border-outline-variant bg-surface px-4 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto`}
          aria-label="Mobile navigation"
        >
          {navItems.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                isActive
                  ? "text-primary font-bold py-3 px-2 rounded-lg bg-surface-container-low"
                  : "text-on-surface-variant hover:text-primary py-3 px-2"
              }
            >
              {label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            onClick={() => setMobileOpen(false)}
            className="mt-2 bg-primary text-on-primary text-center py-3 px-4 rounded-full font-label-md"
          >
            Start Free Trial
          </Link>
        </nav>
      )}
      </div>
    </header>
  );
}

